import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { feedService, interactionsService } from '@/api';
import { useToast } from '@/components/ui';
import { useAuth } from '@/providers';
import type { FeedItem } from '@/types';

export function useFeed() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Busca os itens salvos para mesclar com o feed
  const savedQuery = useQuery({
    queryKey: ['saved', user?.id],
    queryFn: () => interactionsService.getSaved(),
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
  });

  // Busca os itens curtidos para mesclar com o feed
  const likedQuery = useQuery({
    queryKey: ['liked', user?.id],
    queryFn: () => interactionsService.getLiked(),
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
  });

  const feedQuery = useQuery({
    queryKey: ['feed', user?.id],
    queryFn: () => feedService.getFeed(),
    enabled: !!user,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Mescla o estado de salvos e curtidos com o feed
  const items = useMemo(() => {
    const feedItems = feedQuery.data?.items ?? [];
    const savedIds = new Set(savedQuery.data?.items.map((item) => item.content.id) ?? []);
    const likedIds = new Set(likedQuery.data?.items.map((item) => item.content.id) ?? []);

    return feedItems.map((item) => ({
      ...item,
      userState: {
        ...item.userState,
        isSaved: savedIds.has(item.content.id),
        isLiked: likedIds.has(item.content.id),
      },
    }));
  }, [feedQuery.data?.items, savedQuery.data?.items, likedQuery.data?.items]);

  // Optimistic update helper
  const updateFeedItem = (contentId: string, updates: Partial<FeedItem['userState']>) => {
    queryClient.setQueryData(['feed', user?.id], (old: { items: FeedItem[] } | undefined) => {
      if (!old) return old;
      return {
        ...old,
        items: old.items.map((item) =>
          item.content.id === contentId
            ? { ...item, userState: { ...item.userState, ...updates } }
            : item,
        ),
      };
    });
  };

  const removeFromFeed = (contentId: string) => {
    queryClient.setQueryData(['feed', user?.id], (old: { items: FeedItem[] } | undefined) => {
      if (!old) return old;
      return {
        ...old,
        items: old.items.filter((item) => item.content.id !== contentId),
      };
    });
  };

  const likeMutation = useMutation({
    mutationFn: (contentId: string) => interactionsService.like(contentId),
    onMutate: async (contentId) => {
      await queryClient.cancelQueries({ queryKey: ['feed', user?.id] });
      await queryClient.cancelQueries({ queryKey: ['liked', user?.id] });

      const previousFeed = queryClient.getQueryData<{ items: FeedItem[] }>(['feed', user?.id]);
      const previousLiked = queryClient.getQueryData<{ items: FeedItem[] }>(['liked', user?.id]);

      updateFeedItem(contentId, { isLiked: true });

      // Encontra o item no feed para adicionar aos curtidos
      const feedItem = previousFeed?.items.find((item) => item.content.id === contentId);

      // Adiciona ao cache de curtidos
      if (feedItem) {
        queryClient.setQueryData(['liked', user?.id], (old: { items: FeedItem[] } | undefined) => {
          const items = old?.items ?? [];
          if (items.some((item) => item.content.id === contentId)) {
            return old;
          }
          return {
            items: [{ ...feedItem, userState: { ...feedItem.userState, isLiked: true } }, ...items],
          };
        });
      }

      return { previousFeed, previousLiked };
    },
    onError: (_err, _contentId, context) => {
      if (context?.previousFeed) {
        queryClient.setQueryData(['feed', user?.id], context.previousFeed);
      }
      if (context?.previousLiked) {
        queryClient.setQueryData(['liked', user?.id], context.previousLiked);
      }
    },
  });

  const unlikeMutation = useMutation({
    mutationFn: (contentId: string) => interactionsService.unlike(contentId),
    onMutate: async (contentId) => {
      await queryClient.cancelQueries({ queryKey: ['feed', user?.id] });
      await queryClient.cancelQueries({ queryKey: ['liked', user?.id] });

      const previousFeed = queryClient.getQueryData<{ items: FeedItem[] }>(['feed', user?.id]);
      const previousLiked = queryClient.getQueryData<{ items: FeedItem[] }>(['liked', user?.id]);

      updateFeedItem(contentId, { isLiked: false });

      // Remove do cache de curtidos
      queryClient.setQueryData(['liked', user?.id], (old: { items: FeedItem[] } | undefined) => {
        if (!old) return old;
        return {
          ...old,
          items: old.items.filter((item) => item.content.id !== contentId),
        };
      });

      return { previousFeed, previousLiked };
    },
    onError: (_err, _contentId, context) => {
      if (context?.previousFeed) {
        queryClient.setQueryData(['feed', user?.id], context.previousFeed);
      }
      if (context?.previousLiked) {
        queryClient.setQueryData(['liked', user?.id], context.previousLiked);
      }
    },
  });

  const saveMutation = useMutation({
    mutationFn: (contentId: string) => interactionsService.save(contentId),
    onMutate: async (contentId) => {
      await queryClient.cancelQueries({ queryKey: ['feed', user?.id] });
      await queryClient.cancelQueries({ queryKey: ['saved', user?.id] });

      const previousFeed = queryClient.getQueryData<{ items: FeedItem[] }>(['feed', user?.id]);
      const previousSaved = queryClient.getQueryData<{ items: FeedItem[] }>(['saved', user?.id]);

      // Encontra o item no feed para adicionar aos salvos
      const feedItem = previousFeed?.items.find((item) => item.content.id === contentId);

      // Adiciona ao cache de salvos
      if (feedItem) {
        queryClient.setQueryData(['saved', user?.id], (old: { items: FeedItem[] } | undefined) => {
          const items = old?.items ?? [];
          if (items.some((item) => item.content.id === contentId)) {
            return old;
          }
          return {
            items: [{ ...feedItem, userState: { ...feedItem.userState, isSaved: true } }, ...items],
          };
        });
      }

      return { previousFeed, previousSaved };
    },
    onError: (_err, _contentId, context) => {
      if (context?.previousFeed) {
        queryClient.setQueryData(['feed', user?.id], context.previousFeed);
      }
      if (context?.previousSaved) {
        queryClient.setQueryData(['saved', user?.id], context.previousSaved);
      }
    },
  });

  const unsaveMutation = useMutation({
    mutationFn: (contentId: string) => interactionsService.unsave(contentId),
    onMutate: async (contentId) => {
      await queryClient.cancelQueries({ queryKey: ['feed', user?.id] });
      await queryClient.cancelQueries({ queryKey: ['saved', user?.id] });

      const previousFeed = queryClient.getQueryData<{ items: FeedItem[] }>(['feed', user?.id]);
      const previousSaved = queryClient.getQueryData<{ items: FeedItem[] }>(['saved', user?.id]);

      // Remove do cache de salvos
      queryClient.setQueryData(['saved', user?.id], (old: { items: FeedItem[] } | undefined) => {
        if (!old) return old;
        return {
          ...old,
          items: old.items.filter((item) => item.content.id !== contentId),
        };
      });

      return { previousFeed, previousSaved };
    },
    onError: (_err, _contentId, context) => {
      if (context?.previousFeed) {
        queryClient.setQueryData(['feed', user?.id], context.previousFeed);
      }
      if (context?.previousSaved) {
        queryClient.setQueryData(['saved', user?.id], context.previousSaved);
      }
    },
  });

  const dismissMutation = useMutation({
    mutationFn: (contentId: string) => interactionsService.dismiss(contentId),
    onMutate: async (contentId) => {
      await queryClient.cancelQueries({ queryKey: ['feed', user?.id] });
      const previous = queryClient.getQueryData(['feed', user?.id]);
      removeFromFeed(contentId);
      return { previous };
    },
    onError: (_err, _contentId, context) => {
      queryClient.setQueryData(['feed', user?.id], context?.previous);
    },
    onSuccess: () => {
      // Refetch feed to fill the gap with fresh content
      queryClient.invalidateQueries({ queryKey: ['feed', user?.id] });
    },
  });

  const feedbackMutation = useMutation({
    mutationFn: (contentId: string) =>
      interactionsService.feedback({ contentId, reason: 'not_relevant' }),
    onSuccess: (_data, contentId) => {
      removeFromFeed(contentId);
      toast('Feedback registrado', 'success');
      // Refetch feed to fill the gap with fresh content
      queryClient.invalidateQueries({ queryKey: ['feed', user?.id] });
    },
  });

  const muteMutation = useMutation({
    mutationFn: ({ type, value }: { type: 'author' | 'source'; value: string }) =>
      interactionsService.mute({ type, value }),
    onSuccess: () => {
      toast('Preferência salva', 'success');
      queryClient.invalidateQueries({ queryKey: ['feed'] });
    },
  });

  const toggleLike = useCallback(
    (contentId: string, isLiked: boolean) => {
      if (isLiked) {
        unlikeMutation.mutate(contentId);
      } else {
        likeMutation.mutate(contentId);
      }
    },
    [unlikeMutation, likeMutation],
  );

  const toggleSave = useCallback(
    (contentId: string, isSaved: boolean) => {
      if (isSaved) {
        unsaveMutation.mutate(contentId);
      } else {
        saveMutation.mutate(contentId);
      }
    },
    [unsaveMutation, saveMutation],
  );

  return {
    items,
    hasMore: feedQuery.data?.hasMore ?? false,
    isLoading: feedQuery.isLoading || savedQuery.isLoading || likedQuery.isLoading,
    isError: feedQuery.isError,
    error: feedQuery.error,
    refetch: feedQuery.refetch,
    toggleLike,
    toggleSave,
    dismiss: dismissMutation.mutate,
    feedback: feedbackMutation.mutate,
    mute: muteMutation.mutate,
  };
}
